/* istanbul ignore file */
import React, { useMemo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { connect } from 'react-redux'
import {
  useApplicationModals,
  useApplicationQueues,
  useApplicationNotifications,
  Footer,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import Filters from '../filtersHeader'
import NoResultsPage from '../noResultsPage'
import { ListingList } from '../listingList'
import SeoContent from '../seoContent'
import MarkerMap from '../markerMap'
import { isTabletBreakpoint } from '../../util/osDetector'
import { updateSelectedId } from '../../actions/assets'
import * as savedAssetActions from '../../actions/savedAsset'
import * as savedSearchActions from '../../actions/savedSearch'
import {
  ONE_CLICK_SAVE_SEARCH_START,
  trackSaveSearch,
} from '../../services/analytics'
import styles from './styles.scss'
const cx = classnames.bind(styles)

// TODO: Move into hooks lib
const useEventListener = (eventType, callback) => {
  const handler = useRef(callback)

  useEffect(() => {
    handler.current = callback
  }, [callback])

  useEffect(() => {
    const boundListener = event => {
      handler.current?.call(null, event)
    }

    window.addEventListener(eventType, boundListener)

    return () => window.removeEventListener(eventType, boundListener)
  }, [eventType, handler])
}

const SearchPanel = ({
  toggleSave,
  assets,
  featureFlags,
  uiFilterGroups,
  currentFilters,
  listingCompatabilityFilters,
  defaultUiFilterGroups,
  onFilterChange,
  savedSearch,
  updateSelectedId,
  openSaveSearchLightbox,
  user,
}) => {
  const notifications = useApplicationNotifications()
  const modals = useApplicationModals()
  const queues = useApplicationQueues()

  const updateOpenedAsset = openedAssetId => {
    onFilterChange({ openedAssetId })
  }

  const updateSorting = sort => {
    onFilterChange({ sort })
  }

  const checkSaved = (listing = {}) => {
    return listing?.primary_property?.is_currently_saved
  }

  const toggleSaveAsset = ({
    listing,
    updateListingSavedCallback,
    notifications,
    ...analyticsData
  }) => {
    if (!user?.logged_in) {
      queues.postAuthentication.push([
        ({ authType }) => {
          toggleSave({
            listing,
            authType,
            updateListingSavedCallback,
            notifications,
            ...analyticsData,
          })
        },
      ])
      modals.open('authentication')
    } else {
      toggleSave({
        listing,
        updateListingSavedCallback,
        notifications,
        ...analyticsData,
      })
    }
  }

  const onSaveSearch = () => {
    // SRCH-202 saved search start should be tracked regardless of logged_in status
    trackSaveSearch({
      event: ONE_CLICK_SAVE_SEARCH_START,
      user,
    })

    const blueprintData = {
      listingCompatabilityFilters,
      currentFilters,
    }

    if (!user.logged_in) {
      queues.postOnboarding.push([
        () =>
          openSaveSearchLightbox({
            notifications,
            blueprintData,
          }),
      ])
      modals.open('authentication')
    } else {
      openSaveSearchLightbox({ notifications, blueprintData })
    }
  }

  const { mapView: isMapView } = uiFilterGroups.mapFilterGroup

  const [isMapFirstLoaded, setIsMapFirstLoaded] = useState(
    isMapView || !isTabletBreakpoint()
  )

  const { list: assetsList, fetchProgress } = assets
  const haveNoResults = _.isEmpty(assetsList) && !fetchProgress.loading

  useEventListener('resize', () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    if (!isTabletBreakpoint() && !isMapFirstLoaded) {
      setIsMapFirstLoaded(true)
    }
  })

  useEffect(() => {
    if (uiFilterGroups.mapFilterGroup.mapView && !isMapFirstLoaded) {
      setIsMapFirstLoaded(true)
    }
  }, [uiFilterGroups.mapFilterGroup.mapView, isMapFirstLoaded])

  const locationAndNearbySearchFilterGroup = useMemo(
    () => ({
      ...uiFilterGroups.locationFilterGroup,
      ...uiFilterGroups.nearbySearchFilterGroup,
    }),
    [uiFilterGroups.locationFilterGroup, uiFilterGroups.nearbySearchFilterGroup]
  )

  return (
    <div data-elm-id='search_panel' role='main'>
      <div
        className={cx(
          'asset-container',
          'v2-layout',
          !uiFilterGroups.mapFilterGroup.isMapShown ? 'fill' : ''
        )}
      >
        <Filters
          uiFilterGroups={uiFilterGroups}
          defaultUiFilterGroups={defaultUiFilterGroups}
          onFilterChange={onFilterChange}
          onSaveSearch={onSaveSearch}
          updateSorting={updateSorting}
        />

        <div className={cx('content-container', isMapView ? 'hidden' : '')}>
          {/* TODO: move handling of no results to listinglist component to be inside of the infinite scroller to avoid need for double references of the following components */}
          {haveNoResults ? (
            <>
              <NoResultsPage
                onSaveSearch={onSaveSearch}
                filters={locationAndNearbySearchFilterGroup}
                assets={assets}
                updateFiltersAction={onFilterChange}
              />
              <div className={cx('footer-container')}>
                <hr className={cx('footer-separator')} />
                <SeoContent
                  filters={uiFilterGroups.locationFilterGroup}
                  assets={assets}
                />
                <Footer />
              </div>
            </>
          ) : (
            <div className={cx('asset-list-parent')}>
              <ListingList
                assets={assets}
                filters={locationAndNearbySearchFilterGroup}
                checkSaved={checkSaved}
                featureFlags={featureFlags}
                toggleSaveAsset={toggleSaveAsset}
                updateApplicationFocusedListingId={updateSelectedId}
                updateSorting={updateSorting}
                updateFiltersAction={onFilterChange}
                updateOpenedAsset={updateOpenedAsset}
                savedSearch={savedSearch}
                onSaveSearch={onSaveSearch}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={cx(
          'map-container',
          'v2-layout',
          isMapView ? 'active' : 'hidden',
          !uiFilterGroups.mapFilterGroup.isMapShown ? 'closed' : ''
        )}
      >
        <div className={cx('search-map')}>
          {isMapFirstLoaded && (
            <MarkerMap
              assets={assets}
              filters={uiFilterGroups.mapFilterGroup}
              featureFlags={featureFlags}
              isMapView={Boolean(isMapView)}
              onFilterChange={onFilterChange}
              updateOpenedAsset={updateOpenedAsset}
              toggleSaveAsset={toggleSaveAsset}
            />
          )}
        </div>
      </div>
    </div>
  )
}

SearchPanel.propTypes = {
  assets: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  uiFilterGroups: PropTypes.object.isRequired,
  listingCompatabilityFilters: PropTypes.object.isRequired,
  currentFilters: PropTypes.object.isRequired,
  defaultUiFilterGroups: PropTypes.object.isRequired,
  savedSearch: PropTypes.object.isRequired,
  openSaveSearchLightbox: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  updateSelectedId: PropTypes.func.isRequired,
  toggleSave: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
}

const mapProps = store => {
  return {
    assets: store.assets,
    savedSearch: store.savedSearch,
    featureFlags: store.featureFlags,
    user: store.user || {},
  }
}

const mapActions = {
  toggleSave: savedAssetActions.toggleSave,
  openSaveSearchLightbox: savedSearchActions.openLightbox,
  updateSelectedId,
}

export default connect(mapProps, mapActions)(SearchPanel)
